import {
  Grid,
  Typography,
  Button,
  Box,
  CircularProgress,
} from "@mui/material";
import { useState, useRef } from "react";
import AssignmentIcon from "@mui/icons-material/Assignment";
import { useTheme } from "@mui/material/styles";
import {
  createUploadLegalFile,
  previewUploadLawyers,
} from "../../services/PaymentOrderService";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import DynamicExcelHelpModal from "../General/DynamicExcelHelpModal";
import Tooltip from "@mui/material/Tooltip";
import CardHeader from "../General/CardHeader";
import FileUploadBox from "../General/FileUploadBox";
import GlassCard from "../General/GlassCard";
import OnlineQueryTooltip, {
  useOnlineQueryGate,
} from "../General/OnlineQueryGate";
import { useToast } from "../../context/ToastContext";
import LawyerSessionRequiredDialog from "../General/Dialog/LawyerSessionRequiredDialog";
import useLawyerSessionQueue from "../../hooks/useLawyerSessionQueue";

export default function ChargeNumberLegalFilePayment() {
  const { showToast } = useToast();
  const { online } = useOnlineQueryGate();
  const theme = useTheme();
  const fileInputRef = useRef(null);
  const sessionQueue = useLawyerSessionQueue();

  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ numerosExpedientes: "" });
  const [errors, setErrors] = useState({});
  const [selectedFile, setSelectedFile] = useState(null);
  const [openHelp, setOpenHelp] = useState(false);
  const handleOpenHelp = () => setOpenHelp(true);
  const handleCloseHelp = () => setOpenHelp(false);

  const handleFileSelect = (file) => {
    if (file) {
      const validExtensions = ["xls", "xlsx"];
      const fileExtension = file.name.split(".").pop().toLowerCase();
      if (!validExtensions.includes(fileExtension)) {
        setErrors({
          ...errors,
          numerosExpedientes: "Solo se permiten archivos .xls o .xlsx",
        });
        setSelectedFile(null);
        setForm({ ...form, numerosExpedientes: "" });
        return;
      }
      setErrors({ ...errors, numerosExpedientes: "" });
      setSelectedFile(file);
      setForm({ ...form, numerosExpedientes: file.name });
    }
  };

  const buildFormData = () => {
    const formData = new FormData();
    formData.append("numerosExpedientes", selectedFile);
    return formData;
  };

  const submitFile = async () => {
    setLoading(true);
    try {
      const response = await createUploadLegalFile(buildFormData());

      showToast(response.data.message, "success");
      setForm({ numerosExpedientes: "" });
      setSelectedFile(null);
      setErrors({});

      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    } catch (error) {
      if (error.response?.status === 422 || error.response?.status === 404) {
        showToast(error.response.data?.message || "Ocurrió un error.", "error");
      } else {
        console.log(error);
        showToast("Ocurrió un error inesperado.", "error");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrors({});

    if (!selectedFile) {
      showToast("Debe seleccionar un archivo.", "info");
      return;
    }

    try {
      await sessionQueue.checkAndRun(
        () => previewUploadLawyers(buildFormData()),
        submitFile
      );
    } catch (error) {
      if (error.response?.status === 422 || error.response?.status === 404) {
        showToast(error.response.data?.message || "Ocurrió un error.", "error");
      } else {
        console.log(error);
        showToast("Ocurrió un error inesperado.", "error");
      }
    }
  };

  return (
    <Grid container justifyContent="center" sx={{ mt: 4 }}>
      <Grid size={{ xs: 12, sm: 12, md: 12, lg: 10 }}>

        <GlassCard>
          <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
            <Tooltip title="Ver requisitos del Excel" arrow>
              <HelpOutlineIcon
                sx={{
                  cursor: "pointer",
                  color: theme.palette.primary.main,
                  "&:hover": { color: theme.palette.primary.dark },
                }}
                onClick={handleOpenHelp}
              />
            </Tooltip>
          </Box>

          <CardHeader
            icon={<AssignmentIcon />}
            title="Ordenes de Pagos"
            description="Carga Numeros de Expedientes para Ordenes de Pagos"
          />

          <Box component="form" onSubmit={handleSubmit}>
            <Grid container spacing={3}>
              <Grid size={{ xs: 12 }}>
                <FileUploadBox
                  selectedFile={selectedFile}
                  onFileSelect={handleFileSelect}
                  accept=".xls,.xlsx"
                  placeholder="Seleccionar Excel de Órdenes"
                  helperText="O arrastrá el Excel aquí (.xls, .xlsx)"
                />

                {errors.numerosExpedientes && (
                  <Typography
                    color="error"
                    variant="caption"
                    sx={{ mt: 1, display: "block" }}
                  >
                    {errors.numerosExpedientes}
                  </Typography>
                )}
              </Grid>

              <Grid size={{ xs: 12 }}>
                <OnlineQueryTooltip show={!online} block>
                  <Button
                    variant="contained"
                    color="primary"
                    type="submit"
                    fullWidth
                    disabled={loading || !online || sessionQueue.isPending}
                    sx={{ fontWeight: "bold" }}
                  >
                    {loading ? (
                      <CircularProgress size={24} color="inherit" />
                    ) : (
                      "Cargar Expedientes"
                    )}
                  </Button>
                </OnlineQueryTooltip>
              </Grid>
            </Grid>
          </Box>
        </GlassCard>
      </Grid>

      <DynamicExcelHelpModal
        open={openHelp}
        onClose={handleCloseHelp}
        title="Requisitos del archivo Excel"
        description="El archivo debe ser formato .xls o .xlsx."
        columns={["Código de Expediente"]}
        example={["12345678"]}
      />

      <LawyerSessionRequiredDialog {...sessionQueue.dialogProps} />
    </Grid>
  );
}
